import { Create, getValueFromEvent, useFileUploadState, useSelect, useStepsForm } from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import { Button, Divider, Form, Select, Steps, Tag, Upload, Modal, message } from "antd";
import { useApiUrl } from "@refinedev/core";
import { IAuditUploadOrder, IVessel } from "interfaces";
import { TOKEN_KEY } from "authProvider";
import { useRef, useState } from "react";
import { OrderUploadProgress } from "./components/order-upload-progress";

export const UploadOrdersPage: React.FC = () => {
  const {
    current,
    gotoStep,
    stepsProps,
    formProps,
    saveButtonProps,
    submit,
    query: queryResult,
  } = useStepsForm<IAuditUploadOrder>({
    resource: "audit-upload-order",
  });

  const { isLoading, onChange } = useFileUploadState();
  const apiUrl = useApiUrl();
  const [uploadEnabled, setUploadEnabled] = useState(true);
  const [pendingFile, setPendingFile] = useState<any>(null);
  const [isLargeAvcsOrderConfirmed, setIsLargeAvcsOrderConfirmed] = useState(false);
  const [maxAvcsPeriod, setMaxAvcsPeriod] = useState<number | null>(null);
  const [uploadStatusId, setUploadStatusId] = useState<number | null>(null);
  const largeOrderRef = useRef<{ confirmed: boolean; maxPeriod: number | null }>({
    confirmed: false,
    maxPeriod: null,
  });

  const PERMITTED_ACCOUNTS = (process.env.REACT_APP_AVCS_PERMITTED_EMAILS || "").split(",").filter(Boolean);

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "id",

    pagination: {
      mode: "server",
      pageSize: 100
    }
  });

  const token = localStorage.getItem(TOKEN_KEY);
  const userEmail = localStorage.getItem('emr-email') || '';

  const parseCSV = (text: string): any[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const rows = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }
    
    return rows;
  };

  const validateAVCSOrder = async (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const rows = parseCSV(text);
        
        const hasAVCSWithLongPeriod = rows.some(row => 
          row.product_type === 'AVCSCharts' && parseInt(row.product_period) > 3
        );

        if (hasAVCSWithLongPeriod) {
          const maxPeriod = Math.max(...rows
            .filter(row => row.product_type === 'AVCSCharts')
            .map(row => parseInt(row.product_period) || 0));

          if (PERMITTED_ACCOUNTS.includes(userEmail)) {
            Modal.confirm({
              title: 'AVCS Order Confirmation',
              content: `You are placing an AVCS order for ${maxPeriod} months. Please confirm.`,
              onOk: () => {
                setIsLargeAvcsOrderConfirmed(true);
                setMaxAvcsPeriod(maxPeriod);
                largeOrderRef.current.confirmed = true;
                largeOrderRef.current.maxPeriod = maxPeriod;
                resolve(true);
              },
              onCancel: () => {
                setIsLargeAvcsOrderConfirmed(false);
                setMaxAvcsPeriod(null);
                largeOrderRef.current.confirmed = false;
                largeOrderRef.current.maxPeriod = null;
                resolve(false);
              },
            });
          } else {
            Modal.error({
              title: 'Permission Required',
              content: `You are placing an AVCS order for ${maxPeriod} months. Kindly request an approval via email.`,
              onOk: () => {
                setIsLargeAvcsOrderConfirmed(false);
                setMaxAvcsPeriod(null);
                largeOrderRef.current.confirmed = false;
                largeOrderRef.current.maxPeriod = null;
                resolve(false);
              },
            });
          }
        } else {
          setIsLargeAvcsOrderConfirmed(false);
          setMaxAvcsPeriod(null);
          largeOrderRef.current.confirmed = false;
          largeOrderRef.current.maxPeriod = null;
          resolve(true);
        }
      };
      reader.readAsText(file);
    });
  };

  const sendLargeOrderEmail = async (file: File, confirmedMaxPeriod?: number | null) => {
    try {
      const maxPeriodText =
        typeof (confirmedMaxPeriod ?? maxAvcsPeriod) === 'number'
          ? `${confirmedMaxPeriod ?? maxAvcsPeriod} months`
          : 'unknown';
      const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
      const testHost = process.env.REACT_APP_TEST_HOST || 'test.emariner.app';
      const isTestHost = currentHost === testHost || currentHost === 'localhost';
      const toEmail = process.env.REACT_APP_AVCS_ORDER_RECIPIENT || 'samith@emariner.net';
      const subjectEnvPrefix = isTestHost ? '[TEST] ' : '';
      const subject = `${subjectEnvPrefix}AVCS order (> 3 months) - Max ${maxPeriodText} - ${file.name}`;

      const body = `
          <div style="max-width: 640px; margin: 0 auto; padding: 16px;">
            <h2 style="margin: 0 0 12px; color: #111827;">AVCS order placed (> 3 months)</h2>
            <p style="margin: 0 0 16px; color: #374151;">An AVCS order has been submitted via the eMariner Portal with a period greater than 3 months. Details are below, and the original CSV is attached.</p>
            <table style="width: 100%; border-collapse: collapse;">
              <tbody>
                <tr>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb; background: #f9fafb; width: 35%; font-weight: 600;">User</td>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb;">${userEmail || 'unknown'}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-weight: 600;">Max product period</td>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb;">${maxPeriodText}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-weight: 600;">File</td>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb;">${file.name}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-weight: 600;">Time (UTC)</td>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb;">${new Date().toISOString()}</td>
                </tr>
                <tr>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-weight: 600;">Host</td>
                  <td style="padding: 8px 12px; border: 1px solid #e5e7eb;">${currentHost || 'unknown'}</td>
                </tr>
              </tbody>
            </table>
          </div>`;

      // Read the original uploaded CSV file as UTF-8 text for attachment
      const csvTextContent: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // currentHost, isProduction and toEmail computed above for use in the email body

      const mailOptions: any = {
        from: process.env.REACT_APP_EMAIL_SENDER || 'no-reply@emariner.net',
        to: [toEmail],
        cc: [process.env.REACT_APP_EMAIL_CC || 'samith@emariner.net'],
        subject,
        html: body,
        attachments: [
          {
            filename: file.name,
            content: csvTextContent,
            contentType: 'text/csv',
          },
        ],
      };

      const payload = mailOptions;
      const encoder = new TextEncoder();
      const arrayBufferBody: ArrayBuffer = encoder.encode(JSON.stringify(payload)).buffer as ArrayBuffer;

      await fetch(`${apiUrl}/mail/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: arrayBufferBody,
      });
      // message.success('Notification email queued');
    } catch (err: any) {
      // message.warning('Uploaded, but failed to send notification email');
    }
  };

  const customRequest = async (options: any) => {
    const { onSuccess, onError, file, action, headers } = options;
    
    const isValid = await validateAVCSOrder(file);
    
    if (!isValid) {
      onError(new Error('Order validation failed'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(action, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        onSuccess(data, file);
        setUploadStatusId(data?.uploadStatusId ?? null);
        message.success("Order file uploaded - processing started");
        if (largeOrderRef.current.confirmed) {
          // Fire and forget; do not block the UI
          void sendLargeOrderEmail(file, largeOrderRef.current.maxPeriod);
        }
      } else {
        onError(new Error('Upload failed'));
        message.error("Upload failed");
      }
    } catch (error) {
      onError(error);
      message.error("Upload failed");
    }
  };
  
  const props = {
    name: "file",
    multiple: false,
    maxCount: 1,
    customRequest: customRequest,
    action: `${apiUrl}/upload/orders`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    onChange: onChange,
  };

  const formList = [
    <>
      <Form.Item
        label="Vessel"
        name={["vessel", "id"]}
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          {...vesselSelectProps}
          showSearch
          filterOption={(input, option) =>
            (typeof option?.label === "string" &&
              option.label.toLowerCase().includes(input.toLowerCase()))
          }
        />
      </Form.Item>
    </>,
    <>
      <Form.Item
        name="file"
        valuePropName="file"
        getValueFromEvent={getValueFromEvent}
      >
        <Upload.Dragger {...props} listType="picture">
          <p className="ant-upload-drag-icon">
            <Icons.InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag & drop file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for a single upload of CSV file for placing an order with
            UKHO
          </p>
        </Upload.Dragger>
      </Form.Item>
    </>,
  ];

  return (
    <Create
      breadcrumb={false}
      title="Place Order"
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "submit order with UKHO",
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
        tags: queryResult?.isInitialLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      footerButtons={
        <Button type="primary" disabled hidden>
          Upload
        </Button>
      }
    >
      <Steps {...stepsProps}>
        <Steps.Step title="Select Vessel" />
        <Steps.Step title="Upload Order" />
      </Steps>
      <div>
        <Form {...formProps} layout="vertical" style={{ paddingTop: 20 }}>
          {formList[current]}
        </Form>
        {current === 1 && (
          <OrderUploadProgress uploadStatusId={uploadStatusId} />
        )}
        <>
          {current > 0 && (
            <div
              style={{
                paddingTop: 20,
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current - 1);
                }}
                disabled={isLoading}
              >
                Previous
              </Button>
            </div>
          )}
          {current < formList.length - 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => {
                  gotoStep(current + 1);
                }}
              >
                Next
              </Button>
            </div>
          )}
        </>
      </div>
    </Create>
  );
};
